
import { useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useChemicalSearch, Chemical } from "@/hooks/useChemicalSearch";

interface ChemicalSearchComboboxProps {
  onSelect: (chemical: Chemical) => void;
  placeholder?: string;
}

export const ChemicalSearchCombobox = ({ 
  onSelect, 
  placeholder = "Search chemicals..." 
}: ChemicalSearchComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const { searchTerm, setSearchTerm, results, isLoading, usingGreenSeal } = useChemicalSearch();

  const handleSelect = (chemical: Chemical) => {
    onSelect(chemical);
    setOpen(false);
    setInputValue("");
    setSearchTerm("");
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setSearchTerm(value);
  };

  const getSourceBadgeColor = (source: string = 'Standardly') => {
    switch (source) {
      case 'Green Seal': return 'bg-green-100 text-green-800';
      case 'WELL': return 'bg-blue-100 text-blue-800';
      case 'LEED': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-2">
      {usingGreenSeal && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Badge className="bg-green-100 text-green-800">Green Seal Database Active</Badge>
          <span>Enhanced chemical database available</span>
        </div>
      )}
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              <span className="text-muted-foreground">{placeholder}</span>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput 
              placeholder={placeholder}
              value={inputValue}
              onValueChange={handleInputChange}
            />
            <CommandEmpty>
              {isLoading ? "Searching..." : "No chemicals found."}
            </CommandEmpty>
            {results.length > 0 && (
              <CommandGroup>
                {results.map((chemical) => (
                  <CommandItem
                    key={chemical.id}
                    onSelect={() => handleSelect(chemical)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{chemical.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {chemical.cas_number && `CAS: ${chemical.cas_number}`}
                        {chemical.chemical_type && ` • ${chemical.chemical_type}`}
                      </div>
                    </div>
                    <Badge className={getSourceBadgeColor(chemical.source)}>
                      {chemical.source || 'Standardly'}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
